/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";

// Material Dashboard 2 PRO React base styles
import breakpoints from "assets/theme/base/breakpoints";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import ChatBot from "components/ChatBot";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

// Settings page components
import BaseLayout from "layouts/pages/account/components/BaseLayout";
import CourseHome from "layouts/pages/course-content/components/CourseHome";
import Assessment from "layouts/pages/course-content/components/Assessment";
import CourseModule from "layouts/pages/course-content/components/CourseModule";

// context
import { useMaterialUIController, setOpenChatBot, setChatPrefill } from "context";
import { AuthContext } from "context";
import crudsService from "services/cruds-service";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import CourseFeedback from "./components/CourseFeedback";
import SelectionToolbar from "./components/SelectionToolbar";

import { analytics } from "layouts/dashboards/analytics/analytics";
import { events } from "layouts/dashboards/analytics/events";


function CourseContent() {
  const blockContextMenu = (e) => e.preventDefault();
  const [activeStep, setActiveStep] = useState(0);
  const [controller, dispatch] = useMaterialUIController();
  const { openChatBot } = controller;
  const [user, setUser] = useState("");
  const { setIsAuthenticated, getCurrentUser } = useContext(AuthContext);
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [tabValue, setTabValue] = useState(0);
  const handleSetTabValue = (event, newValue) => setTabValue(newValue);
  const navigate = useNavigate();



  // get the course id from the url
  const location = useLocation();
  const courseId = location.pathname.split("/").pop();
  const [content, setActiveContent] = useState(<></>);


  useEffect(() => {
    const start_time = Date.now();
    const getUserId = async () => {
      const user = await getCurrentUser();
      return user;
    };
    getUserId().then((user) => {
      analytics.track(events.VIEW_PAGE, { page: "course-content", userId: user, courseId: courseId });
    });
    return () => {
      analytics.track(events.PAGE_LEFT, { page: "course-content", userId: user, courseId: courseId, start_time: start_time, end_time: Date.now() });
    }
  }, [courseId]);

  const [courseInformation, setCourseInformation] = useState({
    app_name: "",
    short_description: "",
    app_image_location: "",
    course_names_slides: [],
    course_names_videos: [],
    has_chatbot: false,
    has_quiz: false,
    codelabs: [],
    modules: [],
    external_link: "",
  });

  useEffect(() => {
    async function getCourseInformation(course_id) {
      try {
        const response = await crudsService.getCourse(course_id);
        setCourseInformation(response);
      } catch (error) {
        console.error(error);
        alert("An error occurred. Please try again later.");
        navigate("/course-catalog");
      }
    }
    // returns the course summary page introduction markdown string
    getCourseInformation(courseId);
  }, []);
  useEffect(() => {
    async function checkToken() {
      try {
        let user_id = await getCurrentUser();
        if (!user_id) {
          setIsAuthenticated(false);
          localStorage.removeItem("token");
        }
        return user_id;
      } catch (error) {
        console.error(error);
        alert("An error occurred. Please refresh the page or try again later.");
        navigate("/course-catalog");
      }
    }
    checkToken().then((userId) => setUser(userId));
  }, []);

  const [courseCompletionStatus, setCourseCompletionStatus] = useState(false);
  const [coursePurchaseStatus, setCoursePurchaseStatus] = useState(false);

  useEffect(() => {
    const fetchCourseStatus = async () => {
      if (!user || user === "") return;

      try {
        const purchaseStatus = await crudsService.getCoursePurchaseStatus(courseId, user);

        setCourseCompletionStatus(false);
        setCoursePurchaseStatus(purchaseStatus);
      } catch (error) {
        console.error(error);
        alert("An error occurred. Please refresh the page or try again later.");
        navigate("/course-catalog");
      }
    };

    fetchCourseStatus();
  }, [user]);


  useEffect(() => {
    setActiveContent(
      <CourseHome
        courseInformation={courseInformation}
        courseCompletionStatus={courseCompletionStatus}
        moduleCount={courseInformation.num_modules}
        coursePurchaseStatus={coursePurchaseStatus}
        courseId={courseId}
        user={user}
        paymentLink={courseInformation.external_link}
        switchStep={() => {
          handleSetTabValue(null, 1);
          setActiveContent(
            <CourseModule
              courseName={courseInformation.app_name}
              courseCompletionStatus={courseCompletionStatus}
              coursePurchaseStatus={coursePurchaseStatus}
              courseId={courseId}
              user={user}
              modules={courseInformation.modules}
              paymentLink={courseInformation.external_link}
            />
          );
        }}
      />
    );
  }, [courseCompletionStatus, coursePurchaseStatus, user]);

  // Change the openChatBot state
  const handleChatBotOpen = () => setOpenChatBot(dispatch, !openChatBot);

  useEffect(() => {
    // A function that sets the orientation state of the tabs.
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }

    /** 
     The event listener that's calling the handleTabsOrientation function when resizing the window.
    */
    window.addEventListener("resize", handleTabsOrientation);

    // Call the handleTabsOrientation function to set the state with the initial value.
    handleTabsOrientation();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  const chatBotButton = courseInformation.has_chatbot ? (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="info"
      shadow="sm"
      borderRadius="50% 50% 0% 50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="white"
      sx={{ cursor: "pointer" }}
      onClick={handleChatBotOpen}
    >
      <Icon fontSize="small" color="inherit">
        smart_toy
      </Icon>
    </MDBox>
  ) : (
    <></>
  );
  const getStepContent = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        setActiveContent(
          <CourseHome
            courseInformation={courseInformation}
            courseCompletionStatus={courseCompletionStatus}
            coursePurchaseStatus={coursePurchaseStatus}
            courseId={courseId}
            moduleCount={courseInformation.num_modules}
            paymentLink={courseInformation.external_link}
            switchStep={() => {
              handleSetTabValue(null, 1);
              setActiveContent(
                <CourseModule
                  courseName={courseInformation.app_name}
                  courseCompletionStatus={courseCompletionStatus}
                  coursePurchaseStatus={coursePurchaseStatus}
                  courseId={courseId}
                  modules={courseInformation.modules}
                  paymentLink={courseInformation.external_link}
                />
              );
            }}
          />
        );
        break;
      case 1:
        setActiveContent(
          <CourseModule
            courseName={courseInformation.app_name}
            courseCompletionStatus={courseCompletionStatus}
            coursePurchaseStatus={coursePurchaseStatus}
            courseId={courseId}
            user={user}
            modules={courseInformation.modules}
            paymentLink={courseInformation.external_link}
          />
        );
        break;
      case 2:
        setActiveContent(
          <Assessment
            courseName={courseInformation.app_name}
            courseCompletionStatus={courseCompletionStatus}
            coursePurchaseStatus={coursePurchaseStatus}
            courseId={courseId}
          />
        );
        break;
      case 3:
        setActiveContent(
          <CourseFeedback
            courseName={courseInformation.app_name}
            courseId={courseId}
          />
        );
        break;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ChatBot courseId={courseId} />
      {chatBotButton}
      <SelectionToolbar
        buttonText="Ask QuCopilot"
        disabled={!courseInformation.has_chatbot}
        onAction={(selectedText) => {
          setChatPrefill(dispatch, selectedText)
          if (!openChatBot)
            handleChatBotOpen();
        }}
      >
        <MDBox mt={1} onContextMenu={blockContextMenu} className="allow-select">
          <MDBox mb={3}>
            <AppBar position="static">
              <Tabs
                orientation={tabsOrientation}
                value={tabValue}
                onChange={handleSetTabValue}
              >
                <Tab
                  icon={<Icon>home</Icon>}
                  label="Summary"
                  onClick={() => getStepContent(0)}
                />
                <Tab
                  icon={<Icon>slideshow</Icon>}
                  label={coursePurchaseStatus ? "Modules" : "Modules (Preview)"}
                  onClick={() => getStepContent(1)}
                />
                {courseInformation.has_quiz && (
                  <Tab
                    icon={<Icon>quiz</Icon>}
                    label={
                      coursePurchaseStatus ? (
                        <>Assessment</>
                      ) : (
                        <p onClick={() => analytics.track(events.LOCKED_COURSE_ATTEMPT, { course_id: courseId, assessment_attempt: true, user_id: user })}>
                          Assessment <LockIcon />
                        </p>
                      )
                    }
                    disabled={!coursePurchaseStatus}
                    onClick={() => getStepContent(2)}
                  />
                )}
                <Tab
                  icon={<Icon>feedback</Icon>}
                  label="Feedback"
                  onClick={() => getStepContent(3)}
                />
              </Tabs>
            </AppBar>
          </MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={12}>
              <MDBox mb={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    {content}
                  </Grid>
                </Grid>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </SelectionToolbar>
    </DashboardLayout>
  );
}

export default CourseContent;
